import React from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Fade,
} from "@mui/material";
import PropTypes from "prop-types";
import {
  CommentOutlined,
  CheckCircleOutlined,
  CancelOutlined,
} from "@mui/icons-material";
import AppButton from "../../../Common/AppButton";
import theme from "../../../../utils/theme";

// TODO: move the styling to theme.js once approved
// Dialog styles following the same pattern as other modals in the app
const styles = {
  dialogPaper: {
    borderRadius: 3,
    bgcolor: theme.palette.background.default,
    boxShadow: 10,
    width: 600,
    maxWidth: "90vw",
    maxHeight: "98vh",
    mt: 8,
  },
  titleBox: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    height: 32,
  },
  titleText: {
    fontWeight: 600,
    fontSize: "1.15em",
    lineHeight: 1,
    display: "inline-block",
    verticalAlign: "middle",
  },
  dialogTitle: {
    bgcolor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  dialogContent: {
    paddingTop: 0,
    maxHeight: { xs: "60vh", sm: "70vh", md: "none" },
    overflowY: { xs: "auto", sm: "auto", md: "visible" },
  },
  dialogActions: {
    px: 3,
    pb: 2,
    display: "flex",
    alignItems: "center",
  },
};

/**
 * ActionModal - Reusable modal component for RFI approval/rejection actions
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {string} actionType - Type of action: "approve" or "reject"
 * @param {string} remark - Current remark value
 * @param {function} onRemarkChange - Function to handle remark changes
 * @param {function} onConfirm - Function to handle confirmation action
 */
const ActionModal = ({
  open,
  onClose,
  actionType,
  remark,
  onRemarkChange,
  onConfirm,
}) => {
  const isReject = actionType === "reject";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: styles.dialogPaper,
      }}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Box sx={styles.titleBox}>
          {isReject ? (
            <>
              <CancelOutlined />
              <Typography sx={styles.titleText}>Reject RFI</Typography>
            </>
          ) : (
            <>
              <CheckCircleOutlined />
              <Typography sx={styles.titleText}>Approve RFI</Typography>
            </>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent}>
        <Typography
          variant="subtitle1"
          sx={{
            mb: 2,
            color: theme.palette.text.primary,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CommentOutlined
            sx={{ color: theme.palette.text.secondary, fontSize: 18 }}
          />
          {isReject ? "Provide reason for rejection" : "Add approval remarks"}
        </Typography>

        <TextField
          autoFocus
          multiline
          rows={5}
          fullWidth
          variant="outlined"
          placeholder={
            isReject
              ? "Please provide a detailed reason for rejection..."
              : "Add any remarks or notes for this approval (optional)..."
          }
          value={remark}
          onChange={onRemarkChange}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background.paper,
              "& fieldset": {
                borderColor: theme.palette.grey[300],
              },
              "&:hover fieldset": {
                borderColor: isReject
                  ? theme.palette.error.main
                  : theme.palette.success.main,
              },
              "&.Mui-focused fieldset": {
                borderColor: isReject
                  ? theme.palette.error.main
                  : theme.palette.success.main,
              },
            },
          }}
        />

        {isReject && !remark.trim() && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.error.main,
              fontStyle: "italic",
              display: "block",
              mt: 1,
            }}
          >
            * Reason for rejection is required
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <AppButton buttonType="greyed" label="Cancel" onClick={onClose} />
        <Box sx={{ flex: 1 }} />
        <AppButton
          buttonType={isReject ? "danger" : "primary"}
          label={isReject ? "Confirm Rejection" : "Confirm Approval"}
          onClick={onConfirm}
          disabled={isReject && !remark.trim()}
        />
      </DialogActions>
    </Dialog>
  );
};

ActionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  actionType: PropTypes.oneOf(["approve", "reject"]).isRequired,
  remark: PropTypes.string.isRequired,
  onRemarkChange: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default ActionModal;
